import { useCallback, useEffect, useMemo } from "react";
import type { PriceChangeTypeWithFiat } from "@apac/checkout";
import { IconNameEnum } from "@b2bportal/core-themes";
import { trackEvent } from "@hopper-b2b/api";
import {
  CartQuoteEventType,
  cartQuoteSelectors,
  getChildState,
  useCheckoutState,
  useCheckoutStateSelector,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { CheckoutEvent } from "../../state/events";
import { ErrorModal } from "../ErrorModal";
import { LoadingScreen } from "../LoadingScreen";

export const CheckoutPriceCheck = (props: { onSearchAgain?: () => void }) => {
  const { t, formatFiatCurrency } = useI18nContext();
  const [state, send] = useCheckoutState();
  const childState = getChildState(state.value);
  const priceChange: PriceChangeTypeWithFiat = useCheckoutStateSelector(
    cartQuoteSelectors.getCartQuotePriceChange
  );

  const onSearchAgain = useCallback(() => {
    trackEvent({
      eventName: "hotel_viewed_price_change_snackbar_back",
      properties: {},
    });
    props.onSearchAgain?.();
  }, [props]);

  const onContinue = useCallback(() => {
    trackEvent({
      eventName: "hotel_viewed_price_change_snackbar_continue",
      properties: {},
    });
    send(CartQuoteEventType.ACKNOWLEDGE_PRICE_CHANGE);
  }, [send]);

  const modalProps = useMemo(() => {
    if (priceChange == null) {
      return;
    }

    if (priceChange.hasDifference === true) {
      if (priceChange.isIncrease === true) {
        return {
          title: t("apac.checkoutPriceChange.priceIncrease.title"),
          subtitle: t("apac.checkoutPriceChange.priceIncrease.description", {
            previousPrice: formatFiatCurrency(priceChange.predictedTotalFiat),
            newPrice: formatFiatCurrency(priceChange.priceQuoteTotalFiat),
          }),
          primaryButtonText: t(
            "apac.checkoutPriceChange.priceIncrease.primaryCta"
          ),
          primaryOnClick: onContinue,
          secondaryButtonText: t(
            "apac.checkoutPriceChange.priceIncrease.secondaryCta"
          ),
          secondaryOnClick: onSearchAgain,
          iconName: IconNameEnum.upTrendArrow,
        };
      } else {
        return {
          title: t("apac.checkoutPriceChange.priceDecrease.title"),
          subtitle: t("apac.checkoutPriceChange.priceDecrease.description", {
            previousPrice: formatFiatCurrency(priceChange.predictedTotalFiat),
            newPrice: formatFiatCurrency(priceChange.priceQuoteTotalFiat),
          }),
          primaryButtonText: t(
            "apac.checkoutPriceChange.priceDecrease.primaryCta"
          ),
          primaryOnClick: onContinue,
          iconName: IconNameEnum.downTrendArrow,
        };
      }
    }
  }, [formatFiatCurrency, onContinue, onSearchAgain, priceChange, t]);

  useEffect(() => {
    if (priceChange != null && priceChange.hasDifference !== true) {
      send(CheckoutEvent.NEXT);
    }
  }, [priceChange, send]);

  return (
    <>
      <LoadingScreen isLoading loadingSteps={[{ title: "" }]} />

      {modalProps != null && (
        <ErrorModal open={childState === "waiting"} {...modalProps} />
      )}
    </>
  );
};
